import React, { useCallback, useContext, useEffect } from "react";
import s from "../delivery.scss";
import ButtonBack from "Components/buttonBack/ButtonBack";
import { useHistory, useParams } from "react-router-dom";
import { decodeEmailUrl } from "Utils/strings";
import _ from "lodash";
import { t } from "i18next";
import DetailInfo from "Components/detailInfo/DetailInfo";
import { AuthContext } from "@/App";
import cx from "classnames";
import QboxmailApi from "@/qboxmailApi";
import { displayFormattedDate } from "Utils/dateFormats";
import Button from "Components/button/Button";
import useFormState from "Utils/useFormState";
import WarningBanner from "Components/warningBanner/WarningBanner";
import Loader from "Components/loader/Loader";
import { RestrictionRules } from "./New";
import { alertError, alertSuccess } from "Utils/alerts";
import ShowInfoButton from "Components/showInfoButton/ShowInfoButton";
import ResponsiveButtonGroup from "Components/responsiveButtonGroup/ResponsiveButtonGroup";
import { getAccountsFormData } from "Utils/suppressionListForm";
import useToggleDetailsInfo from "Utils/useToggleDetailsInfo";

const api = new QboxmailApi({ useCookie: true });

const DashboardTable = ({ tableData }) => (
  <div>
    {tableData.map((item) => (
      <div key={item.name} className={s.accountInfoRow}>
        <div className={s.accountInfoRowItem}>{item.name}</div>
        <div className={s.accountInfoRowItem}>{item.value}</div>
      </div>
    ))}
  </div>
);

const Show = () => {
  const history = useHistory();
  const { email: encodedEmail, customerCode } = useParams();
  const auth = useContext(AuthContext);
  const {
    formData,
    setInitialValues,
    setFormFieldValue,
    hasFormChanged,
    resetToInitialState,
    isFormLoading,
    setFormLoading,
    unsetFormLoading,
    setFormErrors,
    formErrors,
  } = useFormState({});
  const { isInfoVisible, isInfoToggled, toggleInfo } = useToggleDetailsInfo();

  const email = decodeEmailUrl(encodedEmail);

  const fetchEmailData = useCallback(
    async (email, customerCode) => {
      try {
        return await api.getSuppressionListItem({ email, customer_code: customerCode });
      } catch (error) {
        console.error(error);

        if (error.status === 404) {
          history.replace("/not-found");
        }
      }
    },
    [history]
  );

  const fetchAccountsData = useCallback(
    async (customerCode) => {
      try {
        return await api.getDeliveryAccounts({ customer_code: customerCode });
      } catch (error) {
        console.error(error);

        if (error.status === 404) {
          history.replace("/not-found");
        }
      }
    },
    [history]
  );

  const fetchData = useCallback(
    async (email, customerCode) => {
      try {
        const resp = await Promise.all([fetchEmailData(email, customerCode), fetchAccountsData(customerCode)]);

        const emailData = resp[0].resources[0];
        const selectedAccountsIds = emailData.accounts;
        const availableAccounts = resp[1].resources;

        const selectedAccounts = {};
        selectedAccountsIds
          ? availableAccounts
              .filter((account) => selectedAccountsIds.includes(account.id))
              .forEach((item) => Object.assign(selectedAccounts, { [item.id]: true }))
          : availableAccounts.forEach((item) => Object.assign(selectedAccounts, { [item.id]: true }));

        setInitialValues({
          active: emailData.active,
          created_at: emailData.created_at,
          reason: emailData.reason,
          category: emailData.category,
          message: emailData.message_translated || emailData.message,
          accounts: selectedAccounts,
          availableAccounts: resp[1].resources,
          blockAllAccounts: !selectedAccountsIds || selectedAccountsIds.length === 0,
        });
      } catch (error) {
        console.error("ERROR", error);
      }
    },
    [setInitialValues, fetchEmailData, fetchAccountsData]
  );

  useEffect(() => {
    fetchData(email, customerCode);
  }, [email, customerCode, fetchData]);

  const validate = () => {
    let errors = {};

    if (!formData.blockAllAccounts && getAccountsFormData(formData).length === 0) {
      errors = { ...errors, rules: t("errorMsg.select.deliveryAccount") };
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }

    try {
      setFormLoading();
      await api.updateSuppressionListItem({
        email,
        customer_code: customerCode,
        accounts: formData.blockAllAccounts ? [] : getAccountsFormData(formData),
      });
      alertSuccess(t("alert.suppressionList.edit.success"));

      setInitialValues({
        ...formData,
      });
      unsetFormLoading();
    } catch (error) {
      console.error(error);
      unsetFormLoading();
      alertError(t("alert.wrong"));
    }
  };

  const goBack = () => {
    const { state: locationState } = history.location;
    if (locationState && locationState.fromList) {
      history.goBack();
    } else {
      history.push("/delivery/suppression-list");
    }
  };

  return (
    <div className="flexColumnContainer">
      <div className={s.navigation}>
        <ButtonBack onClick={goBack} />
      </div>

      <div className={s.header}>
        <div className={s.titleContainer}>
          <div className={cx("sectionTitle", auth.isLoggedUserAdmin() ? "marginLeft12" : "")}>{email}</div>
          <ShowInfoButton onClick={toggleInfo} isInfoOpen={isInfoToggled} />
        </div>

        {isInfoVisible && (
          <div className={s.info}>
            <DetailInfo label={t("detailInfo.label.created")} info={displayFormattedDate(formData.created_at)} />
            <DetailInfo label={t("detailInfo.label.type")} info={formData.reason} />
          </div>
        )}
      </div>

      <div className={s.settings}>
        <div className={s.scrollTabs}>
          <div className={s.tabs}>
            <div className={cx(s.itemTab, { [s.active]: true })}>{t("tab.details")}</div>
          </div>
        </div>
      </div>

      <div className="showPageContent">
        <div className={cx(s.dashboardContainer, s.noMarginTop)}>
          <div className={s.dashboardHeader}>
            <ResponsiveButtonGroup
              buttonElements={[
                hasFormChanged ? (
                  <Button onClick={resetToInitialState} color="outline" key={0}>
                    button.cancel
                  </Button>
                ) : null,
                <Button loading={isFormLoading} onClick={submit} key={1}>
                  button.save
                </Button>,
              ]}
              warningElement={hasFormChanged && <WarningBanner color="yellow-text" msg={t("infoMsg.unsaveChanged")} />}
              className="expand flex justify-end items-center gap20"
            />
          </div>
          <div className={s.dashboardContent}>
            <div className={s.dashboardItem1}>
              <div className="settingTitle">{t("delivery.suppressionList.bounce.title")}</div>
              <div className="sectionDesc">{t("delivery.suppressionList.bounce.desc")}</div>
            </div>
            <div className={s.dashboardItem2}>
              <DashboardTable
                tableData={_.compact([
                  {
                    name: t("detailInfo.label.suppressionReason"),
                    value: formData.reason || "-",
                  },
                  formData.reason === "bounce" && {
                    name: t("detailInfo.label.category"),
                    value: formData.category || "-",
                  },
                  formData.reason === "bounce" && {
                    name: t("detailInfo.label.message"),
                    value: formData.message || "-",
                  },
                ])}
              />
            </div>
          </div>
          <div className={s.dashboardContent}>
            <div className={s.dashboardItem1}>
              <div className="settingTitle">{t("delivery.suppressionList.details.title")}</div>
              <div className="sectionDesc">{t("delivery.suppressionList.details.desc")}</div>
            </div>
            <div className={s.dashboardItem2}>
              {!formData.accounts || !formData.availableAccounts ? (
                <Loader />
              ) : (
                <>
                  <RestrictionRules
                    accounts={formData.availableAccounts}
                    showTitle={false}
                    checkedAccounts={formData.accounts}
                    onSelectAllChange={(value) => setFormFieldValue({ blockAllAccounts: value })}
                    onChange={({ accounts }) => {
                      setFormFieldValue({ accounts });
                    }}
                    allSelected={formData.blockAllAccounts}
                  />
                  {formErrors.rules && <label className={s.errorText}>{formErrors.rules}</label>}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Show;
